"use client";

import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, MoveHorizontal } from "lucide-react";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const projects = [{
  id: 1,
  name: "Industrial Control Panel",
  location: "ANODIZED ALUMINUM",
  timeline: "24 HOURS",
  area: "PRECISION ETCH",
  plants: "UL 508A COMPLIANT",
  beforeImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/raw_aluminum_stock.png",
  beforeSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/raw_aluminum_stock.png-wvc-srcset",
  beforeSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/raw_aluminum_stock.png-wvc-sizes",
  afterImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/finished_anodized_panel.png",
  afterSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/finished_anodized_panel.png-wvc-srcset",
  afterSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/finished_anodized_panel.png-wvc-sizes"
}, {
  id: 2,
  name: "Marine Grade Tagging",
  location: "HARD COAT ANODIZED",
  timeline: "48 HOURS",
  area: "CORROSION RESISTANT",
  plants: "SALT SPRAY TESTED",
  beforeImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/marine_blank_plate.webp",
  beforeSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/marine_blank_plate.webp-wvc-srcset",
  beforeSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/marine_blank_plate.webp-wvc-sizes",
  afterImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/marine_engraved_tag.png",
  afterSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/marine_engraved_tag.png-wvc-srcset",
  afterSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/marine_engraved_tag.png-wvc-sizes"
}, {
  id: 3,
  name: "Aerospace Component",
  location: "BLACK ANODIZED",
  timeline: "72 HOURS",
  area: "LASER MARKED",
  plants: "AS9100 STANDARDS",
  beforeImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/aerospace_raw_part.png",
  beforeSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/aerospace_raw_part.png-wvc-srcset",
  beforeSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/aerospace_raw_part.png-wvc-sizes",
  afterImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/aerospace_finished_part.png",
  afterSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/aerospace_finished_part.png-wvc-srcset",
  afterSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/aerospace_finished_part.png-wvc-sizes"
}, {
  id: 4,
  name: "Custom Equipment ID",
  location: "CLEAR ANODIZED",
  timeline: "36 HOURS",
  area: "HIGH CONTRAST",
  plants: "DURABLE FINISH",
  beforeImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/equipment_blank_sheet.webp",
  beforeSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/equipment_blank_sheet.webp-wvc-srcset",
  beforeSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/equipment_blank_sheet.webp-wvc-sizes",
  afterImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/equipment_engraved_id.webp",
  afterSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/equipment_engraved_id.webp-wvc-srcset",
  afterSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/equipment_engraved_id.webp-wvc-sizes"
}];
export default function WhenToChooseAnodizedAluminum() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isFading, setIsFading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: featuredRef,
    isVisible: featuredVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: detailsRef,
    isVisible: detailsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const active = projects[activeIndex];
  const handleThumbnailClick = (index: number) => {
    if (index === activeIndex) return;
    setIsFading(true);
    setTimeout(() => {
      setActiveIndex(index);
      setSliderPosition(50);
      setIsFading(false);
    }, 250);
  };
  const updatePosition = (clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percent = Math.max(0, Math.min(100, x / rect.width * 100));
    setSliderPosition(percent);
  };
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleTouchStart = () => {
    setIsDragging(true);
  };
  useEffect(() => {
    if (!isDragging) return;
    const handleMouseMove = (e: MouseEvent) => updatePosition(e.clientX);
    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches[0]) updatePosition(e.touches[0].clientX);
    };
    const handleEnd = () => setIsDragging(false);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleEnd);
    window.addEventListener("touchmove", handleTouchMove);
    window.addEventListener("touchend", handleEnd);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleEnd);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleEnd);
    };
  }, [isDragging]);
  return <section data-section-id="282" id="when-to-choose-anodized-aluminum" className="bg-background text-foreground py-20 md:py-32 px-4 md:px-8 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: "radial-gradient(var(--foreground) 1px, transparent 1px)",
      backgroundSize: "32px 32px"
    }} aria-hidden="true" />

      <div className="max-w-7xl mx-auto relative">
        <div ref={headerRef} className={`text-center mb-16 md:mb-20 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-primary mb-6">
            — Material Excellence —
          </p>
          <h2 className="font-serif font-default text-4xl md:text-5xl lg:text-6xl font-light leading-tight mb-6 text-foreground">
            The <em className="italic font-normal text-primary">Anodized</em> Advantage
          </h2>
          <p className="max-w-2xl mx-auto text-base md:text-lg text-muted-foreground font-sans leading-relaxed">
            Discover why anodized aluminum is the industry standard for durability. 
            Compare raw material surfaces with our precision-finished, laser-marked results.
          </p>
        </div>

        <div ref={featuredRef} className={`mb-12 transition-all duration-1000 delay-150 ease-out ${featuredVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div ref={containerRef} className="relative rounded-lg overflow-hidden select-none shadow-lg border border-border">
            <AspectRatio ratio={16 / 9}>
              <div className={`absolute inset-0 transition-opacity duration-500 ${isFading ? "opacity-0" : "opacity-100"}`}>
                <img src={active.afterImage} data-wvc-srcset={active.afterSrcset} data-wvc-sizes={active.afterSizes} alt={`${active.name} finished`} className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" />

                <div className="absolute inset-0 overflow-hidden" style={{
                clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`
              }}>
                  <img src={active.beforeImage} data-wvc-srcset={active.beforeSrcset} data-wvc-sizes={active.beforeSizes} alt={`${active.name} raw`} className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" />
                </div>

                <div className="absolute top-4 left-4 md:top-6 md:left-6 z-10">
                  <span className="font-mono text-[10px] md:text-xs tracking-[0.25em] uppercase bg-background/80 backdrop-blur-sm text-foreground px-3 py-1.5 rounded-md border border-border">
                    Raw
                  </span>
                </div>
                <div className="absolute top-4 right-4 md:top-6 md:right-6 z-10">
                  <span className="font-mono text-[10px] md:text-xs tracking-[0.25em] uppercase bg-primary/90 backdrop-blur-sm text-primary-foreground px-3 py-1.5 rounded-md">
                    Finished
                  </span>
                </div>

                <div className="absolute top-0 bottom-0 w-0.5 bg-secondary cursor-ew-resize z-20" style={{
                left: `${sliderPosition}%`
              }} onMouseDown={handleMouseDown} onTouchStart={handleTouchStart}>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 rounded-lg bg-secondary shadow-lg flex items-center justify-center cursor-grab active:cursor-grabbing border-2 border-background" style={{
                  borderRadius: "8px"
                }}>
                    <ChevronLeft className="w-4 h-4 md:w-5 md:h-5 text-primary -mr-1" strokeWidth={2.5} />
                    <ChevronRight className="w-4 h-4 md:w-5 md:h-5 text-primary -ml-1" strokeWidth={2.5} />
                  </div>
                </div>
              </div>
            </AspectRatio>
          </div>

          <div className="flex items-center justify-center mt-4 gap-2 text-muted-foreground">
            <MoveHorizontal className="w-3.5 h-3.5" />
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase">
              Slide to compare
            </span>
          </div>
        </div>

        <div className="mb-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {projects.map((project, i) => <Button key={project.id} data-index={i} onClick={() => handleThumbnailClick(i)} variant="ghost" className={`relative h-auto p-0 rounded-lg overflow-hidden border-2 transition-all duration-300 flex flex-col ${activeIndex === i ? "border-primary shadow-md scale-[1.02]" : "border-border hover:border-secondary"}`}>
                <AspectRatio ratio={4 / 3} className="w-full">
                  <div className="relative w-full h-full">
                    <img src={project.afterImage} data-wvc-srcset={project.afterSrcset} data-wvc-sizes={project.afterSizes} alt={`${project.name} preview`} className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" />
                    <div className="absolute inset-0 overflow-hidden" style={{
                  clipPath: "inset(0 50% 0 0)"
                }}>
                      <img src={project.beforeImage} data-wvc-srcset={project.beforeSrcset} data-wvc-sizes={project.beforeSizes} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" />
                    </div>
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-px h-full bg-secondary/80" />

                    {activeIndex === i && <div className="absolute inset-0 bg-primary/10" />}
                  </div>
                </AspectRatio>
                <div className="px-3 py-2.5 bg-card text-left w-full">
                  <p className="font-serif font-default text-sm md:text-base text-card-foreground truncate">
                    {project.name}
                  </p>
                  <p className="font-mono text-[9px] md:text-[10px] tracking-[0.2em] uppercase text-muted-foreground mt-0.5 truncate">
                    {project.location}
                  </p>
                </div>
              </Button>)}
          </div>
        </div>

        <div ref={detailsRef} className={`max-w-3xl mx-auto text-center transition-all duration-700 ease-out ${detailsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div key={active.id} className="animate-in fade-in duration-500">
            <h3 className="font-serif font-default text-3xl md:text-4xl font-light text-foreground mb-2">
              {active.name}
            </h3>
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-muted-foreground mb-8">
              {active.location}
            </p>

            <div className="grid grid-cols-3 gap-4 md:gap-8 mb-10 py-8 border-y border-border">
              <div>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2">
                  Lead Time
                </p>
                <p className="font-serif font-default text-lg md:text-xl text-primary">
                  {active.timeline}
                </p>
              </div>
              <div>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2">
                  Process
                </p>
                <p className="font-serif font-default text-lg md:text-xl text-primary">
                  {active.area}
                </p>
              </div>
              <div>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2">
                  Compliance
                </p>
                <p className="font-serif font-default text-lg md:text-xl text-primary">
                  {active.plants}
                </p>
              </div>
            </div>

            <Button asChild variant="link" className="font-mono text-xs tracking-[0.25em] uppercase text-primary hover:text-primary/80">
              
            </Button>
          </div>
        </div>
      </div>
    </section>;
}